// Voice Broadcast — agentless message blasting console.
// Upload-less config: pick an Audio Prompt + Lead List, set concurrency,
// AMD, retry, and a DTMF key map. Start/Pause/Resume/Stop with live counters.
import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Loader2, Megaphone, Play, Pause, Square, Download, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import RelationalPicker from "@/admin/components/RelationalPicker";
import FileUpload from "@/admin/components/FileUpload";
import CallingWindowEditor, { CallingWindowPill } from "@/admin/components/CallingWindowEditor";

const DTMF_ACTIONS = [
  { v: "capture",  label: "Capture digit (feedback / survey)" },
  { v: "transfer", label: "Transfer to a queue" },
  { v: "repeat",   label: "Repeat the message" },
  { v: "optout",   label: "Opt-out (add to DNC)" },
];

function defaults() {
  return {
    mode: "voice_broadcast",
    audio_prompt_id: "", lead_list_id: "", trunk_id: "", caller_id_pool_id: "",
    concurrent_channels: 5, amd_enabled: false,
    amd_on_machine: "tenant", amd_voicemail_audio_id: "",
    max_attempts: 2, retry_gap_minutes: 60, skip_compliance: false,
    dtmf: { enabled: false, keys: [] },
    calling_window: {},
  };
}

export default function VoiceBroadcastPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults());
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [leadLists, setLeadLists] = useState([]);
  const { canWrite, canDelete } = useCurrentModulePerms();
  useEffect(() => { api.get("/admin/resources/lead_lists").then((r) => setLeadLists(r.data || [])).catch(() => {}); }, []);
  const mappedLists = (id) => leadLists.filter((l) => l.data?.campaign_id === id);

  async function load() {
    setLoading(true);
    try { const r = await api.get("/broadcast/campaigns"); setRows(r.data || []); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); const i = setInterval(load, 5000); return () => clearInterval(i); }, []);

  function startNew() { setForm(defaults()); setName(""); setActive(true); setEditing("new"); }
  function startEdit(r) {
    const d = r.data || {};
    setForm({ ...defaults(), ...Object.fromEntries(Object.entries(d).filter(([k]) => k in defaults())),
              dtmf: { enabled: !!(d.dtmf?.enabled), keys: d.dtmf?.keys || [] } });
    setName(r.name || ""); setActive(r.is_active !== false); setEditing(r);
  }

  async function save() {
    if (!name.trim()) { toast.error("Name required"); return; }
    if (form.amd_enabled && form.amd_on_machine === "voicemail" && !form.amd_voicemail_audio_id) {
      toast.error("Pick a voicemail audio prompt for the answering-machine message"); return;
    }
    setSaving(true);
    try {
      const payload = { name: name.trim(), is_active: active, data: { ...form, mode: "voice_broadcast" } };
      if (editing === "new") await api.post("/admin/resources/campaigns", payload);
      else await api.patch(`/admin/resources/campaigns/${editing.id}`, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }
  async function remove(r) {
    if (!confirm(`Delete "${r.name}"?`)) return;
    try { await api.delete(`/admin/resources/campaigns/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  // DTMF key editing
  function addKey() {
    setForm((f) => ({ ...f, dtmf: { ...f.dtmf, keys: [...(f.dtmf.keys || []), { digit: "", action: "capture", label: "", transfer_target_id: "" }] } }));
  }
  function updKey(i, patch) {
    setForm((f) => { const keys = [...f.dtmf.keys]; keys[i] = { ...keys[i], ...patch }; return { ...f, dtmf: { ...f.dtmf, keys } }; });
  }
  function delKey(i) {
    setForm((f) => ({ ...f, dtmf: { ...f.dtmf, keys: f.dtmf.keys.filter((_, j) => j !== i) } }));
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="vb-title">Voice Broadcast</h1>
          <p className="text-sm text-muted-foreground mt-1">Agentless campaigns: dial a lead list, play a recorded message, and capture DTMF responses. No agents required.</p>
        </div>
        {canWrite && <Button data-testid="vb-add-btn" onClick={startNew} className="gap-1.5"><Plus className="w-4 h-4"/> New broadcast</Button>}
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">No voice broadcasts yet. Click <strong>New broadcast</strong> to create one.</Card>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {rows.map((r) => <BroadcastCard key={r.id} row={r} onEdit={() => startEdit(r)} onDelete={() => remove(r)} onChange={load} canWrite={canWrite} canDelete={canDelete} />)}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New voice broadcast" : `Edit ${editing?.name}`}</DialogTitle>
            <DialogDescription>Pick the message + lead list, set pacing, and configure DTMF responses.</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="basics" className="py-2">
            <TabsList>
              <TabsTrigger value="basics" data-testid="vb-tab-basics">Basics</TabsTrigger>
              <TabsTrigger value="pacing" data-testid="vb-tab-pacing">Pacing</TabsTrigger>
              <TabsTrigger value="dtmf" data-testid="vb-tab-dtmf">DTMF capture</TabsTrigger>
            </TabsList>

            <TabsContent value="basics" className="space-y-3 mt-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2"><Label>Name</Label><Input data-testid="vb-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
                <div className="flex items-center gap-2 mt-7"><Switch data-testid="vb-active" checked={active} onCheckedChange={setActive} /><Label>Active</Label></div>
              </div>
              <div><Label>Voice message (audio prompt)</Label><RelationalPicker kind="audio_prompts" value={form.audio_prompt_id} onChange={(v) => setForm({ ...form, audio_prompt_id: v })} testId="vb-audio" /></div>
              <div>
                <Label>Mapped lead lists</Label>
                <div className="min-h-9 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm" data-testid="vb-mapped-lists">
                  {editing && editing !== "new"
                    ? (mappedLists(editing.id).length
                        ? <div className="flex flex-wrap gap-1.5">{mappedLists(editing.id).map((l) => <Badge key={l.id} variant="secondary" className="text-[11px]">{l.name} <span className="opacity-60 ml-1">({l.data?.contacts_count ?? 0})</span></Badge>)}</div>
                        : <span className="text-amber-600 text-xs">No lists mapped — map from Admin → Outbound → Lead Lists</span>)
                    : <span className="text-muted-foreground text-xs">Save this broadcast, then map lists to it from the Lead Lists page.</span>}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Voice broadcast needs only phone + first name; extra fields are optional at upload.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>SIP trunk</Label><RelationalPicker kind="sip_trunks" value={form.trunk_id} onChange={(v) => setForm({ ...form, trunk_id: v })} testId="vb-trunk" /></div>
                <div><Label>Caller-ID pool</Label><RelationalPicker kind="caller_id_pool" value={form.caller_id_pool_id} onChange={(v) => setForm({ ...form, caller_id_pool_id: v })} testId="vb-cidpool" /></div>
              </div>
            </TabsContent>

            <TabsContent value="pacing" className="space-y-3 mt-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label>Concurrent channels</Label>
                  <Input data-testid="vb-concurrency" type="number" min="1" max="200" value={form.concurrent_channels}
                    onChange={(e) => setForm({ ...form, concurrent_channels: Math.max(1, Math.min(200, Number(e.target.value) || 1)) })} />
                  <p className="text-[11px] text-muted-foreground mt-1">Max simultaneous calls. Respects the trunk's max-channels / CPS limit.</p>
                </div>
                <div><Label>Max attempts</Label><Input data-testid="vb-maxattempts" type="number" min="1" value={form.max_attempts} onChange={(e) => setForm({ ...form, max_attempts: Math.max(1, Number(e.target.value) || 1) })} /></div>
                <div><Label>Retry gap (min)</Label><Input data-testid="vb-retrygap" type="number" min="0" value={form.retry_gap_minutes} onChange={(e) => setForm({ ...form, retry_gap_minutes: Math.max(0, Number(e.target.value) || 0) })} /></div>
              </div>
              <div className="flex items-center gap-2"><Switch data-testid="vb-amd" checked={!!form.amd_enabled} onCheckedChange={(v) => setForm({ ...form, amd_enabled: !!v })} /><Label>Answering machine detection (AMD)</Label></div>
              {form.amd_enabled && (
                <div className="pl-8 grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">On answering machine</Label>
                    <Select value={form.amd_on_machine || "tenant"} onValueChange={(v) => setForm({ ...form, amd_on_machine: v })}>
                      <SelectTrigger data-testid="vb-amd-action" className="h-9"><SelectValue placeholder="Use tenant default" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tenant">Use tenant AMD default</SelectItem>
                        <SelectItem value="hangup">Hang up (no message)</SelectItem>
                        <SelectItem value="voicemail">Leave voicemail message</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.amd_on_machine === "voicemail" && (
                    <div>
                      <Label className="text-xs">Voicemail audio (machine)</Label>
                      <FileUpload kind="audio_prompts" value={form.amd_voicemail_audio_id || ""} onChange={(url) => setForm({ ...form, amd_voicemail_audio_id: url })} accept="audio/*" testId="vb-amd-vm" />
                    </div>
                  )}
                  <p className="text-[11px] text-muted-foreground sm:col-span-2">Detection sensitivity is configured tenant-wide in Admin → AMD Settings.</p>
                </div>
              )}
              <div className="pt-2 border-t border-amber-200 dark:border-amber-500/20"><div className="flex items-center gap-2"><Switch data-testid="vb-skipcomp" checked={!!form.skip_compliance} onCheckedChange={(v) => setForm({ ...form, skip_compliance: !!v })} /><Label className="text-amber-700 dark:text-amber-300">Override regulatory compliance window</Label></div><p className="text-[11px] text-muted-foreground mt-1">When ON, broadcast hours are governed only by this campaign&apos;s Calling Window below; the per-region regulatory default is ignored. Set a Calling Window before enabling &mdash; otherwise blasting is unrestricted.</p></div>
              <div className="pt-3 border-t border-border">
                <CallingWindowEditor value={form.calling_window} onChange={(cw) => setForm({ ...form, calling_window: cw })} testPrefix="vb-cw" />
              </div>
            </TabsContent>

            <TabsContent value="dtmf" className="space-y-3 mt-3">
              <div className="flex items-center gap-2"><Switch data-testid="vb-dtmf-enabled" checked={!!form.dtmf?.enabled} onCheckedChange={(v) => setForm({ ...form, dtmf: { ...form.dtmf, enabled: !!v } })} /><Label>Capture DTMF keypresses during the message</Label></div>
              {form.dtmf?.enabled && (
                <div className="space-y-2">
                  {(form.dtmf.keys || []).map((k, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center p-2 rounded-md border border-border" data-testid={`vb-dtmf-row-${i}`}>
                      <div className="col-span-2"><Input data-testid={`vb-dtmf-digit-${i}`} placeholder="Key" maxLength={1} value={k.digit} onChange={(e) => updKey(i, { digit: e.target.value.replace(/[^0-9*#]/g, "") })} className="h-9 text-center font-mono" /></div>
                      <div className="col-span-4">
                        <Select value={k.action} onValueChange={(v) => updKey(i, { action: v })}>
                          <SelectTrigger data-testid={`vb-dtmf-action-${i}`} className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{DTMF_ACTIONS.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-5">
                        {k.action === "transfer" ? (
                          <RelationalPicker kind="queues" value={k.transfer_target_id} onChange={(v) => updKey(i, { transfer_target_id: v })} testId={`vb-dtmf-queue-${i}`} placeholder="Transfer to queue…" />
                        ) : (
                          <Input data-testid={`vb-dtmf-label-${i}`} placeholder="Label (e.g. Satisfied)" value={k.label} onChange={(e) => updKey(i, { label: e.target.value })} className="h-9" />
                        )}
                      </div>
                      <div className="col-span-1 text-right"><Button data-testid={`vb-dtmf-del-${i}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => delKey(i)}><X className="w-4 h-4" /></Button></div>
                    </div>
                  ))}
                  <Button data-testid="vb-dtmf-add" variant="outline" size="sm" onClick={addKey} className="gap-1.5"><Plus className="w-3.5 h-3.5" /> Add key</Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="vb-save" onClick={save} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const STATE_BADGE = {
  running: "bg-emerald-600 text-white",
  paused: "bg-amber-500 text-white",
  stopped: "bg-slate-500 text-white",
};

function BroadcastCard({ row, onEdit, onDelete, onChange, canWrite = true, canDelete = true }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  async function loadStatus() {
    try { const r = await api.get(`/broadcast/campaigns/${row.id}/status`); setStatus(r.data); } catch {}
  }
  useEffect(() => { loadStatus(); const i = setInterval(loadStatus, 4000); return () => clearInterval(i); }, [row.id]);

  async function act(verb) {
    setBusy(true);
    try { await api.post(`/broadcast/campaigns/${row.id}/${verb}`); await loadStatus(); onChange?.(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  }

  async function downloadCsv() {
    try {
      const r = await api.get(`/broadcast/results.csv?campaign_id=${row.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${row.name || "broadcast"}_results.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  }

  const st = status?.stats || row.stats || {};
  const state = status?.state || row.run_state || "stopped";
  const lp = status?.list_progress || { total: 0, done: 0, pending: 0, retry: 0 };
  const processed = (lp.done || 0) + (lp.dnc || 0);
  const pct = lp.total ? Math.round((processed / lp.total) * 100) : 0;
  const dtmf = st.dtmf || {};

  return (
    <Card className="p-5 space-y-4" data-testid={`vb-card-${row.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-primary" />
            <h3 className="font-display font-semibold truncate" data-testid={`vb-name-${row.id}`}>{row.name}</h3>
            <Badge className={`text-[10px] uppercase ${STATE_BADGE[state] || ""}`} data-testid={`vb-state-${row.id}`}>{state}</Badge>
          </div>
          {status?.calling_window?.enabled && (
            <div className="mt-1.5"><CallingWindowPill window={status.calling_window} testId={`vb-window-${row.id}`} /></div>
          )}
        </div>
        <div className="flex gap-1">
          {canWrite && <Button data-testid={`vb-edit-${row.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}><Edit2 className="w-4 h-4" /></Button>}
          {canDelete && <Button data-testid={`vb-del-${row.id}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={onDelete}><Trash2 className="w-4 h-4" /></Button>}
        </div>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {canWrite && state === "stopped" && <Button data-testid={`vb-start-${row.id}`} size="sm" onClick={() => act("start")} disabled={busy} className="gap-1.5"><Play className="w-3.5 h-3.5" /> Start</Button>}
        {canWrite && state === "running" && <Button data-testid={`vb-pause-${row.id}`} size="sm" variant="outline" onClick={() => act("pause")} disabled={busy} className="gap-1.5"><Pause className="w-3.5 h-3.5" /> Pause</Button>}
        {canWrite && state === "paused" && <Button data-testid={`vb-resume-${row.id}`} size="sm" onClick={() => act("resume")} disabled={busy} className="gap-1.5"><Play className="w-3.5 h-3.5" /> Resume</Button>}
        {canWrite && (state === "running" || state === "paused") && <Button data-testid={`vb-stop-${row.id}`} size="sm" variant="destructive" onClick={() => act("stop")} disabled={busy} className="gap-1.5"><Square className="w-3.5 h-3.5" /> Stop</Button>}
        <Button data-testid={`vb-csv-${row.id}`} size="sm" variant="outline" onClick={downloadCsv} className="gap-1.5"><Download className="w-3.5 h-3.5" /> Results CSV</Button>
      </div>

      <div className="grid grid-cols-4 gap-3 pt-3 border-t border-border text-sm">
        <Kpi label="Dialed" value={st.dialed ?? 0} />
        <Kpi label="Answered" value={st.answered ?? 0} tone="good" />
        <Kpi label="Machine" value={st.machine ?? 0} />
        <Kpi label="In flight" value={status?.in_flight ?? 0} />
      </div>

      <div className="space-y-1.5">
        <div className="flex justify-between text-[11px] text-muted-foreground">
          <span>List progress</span>
          <span data-testid={`vb-progress-${row.id}`}>{processed} of {lp.total} processed</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
        <div className="flex gap-3 text-[11px] text-muted-foreground flex-wrap">
          <span>no-answer {st.no_answer ?? 0}</span>
          <span>opt-out {st.opted_out ?? 0}</span>
          <span>transferred {st.transferred ?? 0}</span>
        </div>
      </div>

      {Object.keys(dtmf).length > 0 && (
        <div className="pt-2 border-t border-border">
          <div className="text-[10px] uppercase text-muted-foreground mb-1.5">DTMF responses</div>
          <div className="flex gap-2 flex-wrap" data-testid={`vb-dtmf-breakdown-${row.id}`}>
            {Object.entries(dtmf).map(([d, c]) => (
              <Badge key={d} variant="secondary" className="font-mono">{d}: {c}</Badge>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function Kpi({ label, value, tone }) {
  const cls = tone === "good" ? "text-emerald-700 dark:text-emerald-400" : "text-foreground";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold ${cls}`}>{value}</div>
    </div>
  );
}
